/**
 * AR Vision Demo — Custom Aging Report view signal.
 *
 * THROWAWAY DEMO CODE. Bridges the scripted Nami report tools and the Reports
 * page's LOCAL `useArReportState` (view mode / flat sort / customer selection).
 * A tool calls `pushReportDemoView(...)`; the page, via `useApplyReportDemoView`,
 * subscribes and applies the requested view through the real state setters — so
 * "show me overdue invoices oldest first" and "drill into this customer" drive
 * the exact same UI a presenter would click by hand.
 *
 * It's a tiny external store (built on the same listener pattern as
 * `fixture-store`) carrying a monotonically increasing `token`, so re-issuing
 * the same view (e.g. asking twice) still re-applies. Delete alongside
 * frontend/src/demo/ar.
 */

import type { ArAgingViewMode, ArInvoiceFlatSortBy } from '@erp/rpc';

/** A requested report view. Omitted fields are left untouched on the page. */
export interface ReportDemoView {
  viewMode?: ArAgingViewMode;
  flatSortBy?: ArInvoiceFlatSortBy;
  flatSortDir?: 'asc' | 'desc';
  /** Narrow the flat view to a single customer (drill-in). */
  customerId?: string;
  customerName?: string;
  /** Set monotonically by the store so repeated identical pushes re-apply. */
  token: number;
}

let current: ReportDemoView | null = null;
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Ask the mounted Reports page to apply a view (a scripted tool calls this). */
export function pushReportDemoView(view: Omit<ReportDemoView, 'token'>): void {
  seq += 1;
  current = { ...view, token: seq };
  emit();
}

/** Current requested view (null until a tool pushes one). */
export function getReportDemoView(): ReportDemoView | null {
  return current;
}

/** Subscribe to view requests. Returns an unsubscribe fn. */
export function subscribeReportDemoView(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
